import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { maps, baseURL } from "../../Constants";
import { Match } from "../../models/Match";
import { getMatchById } from "../../services/MatchService";
import axios from "axios";



const AddVetoPage: React.FC = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const navigate = useNavigate();
    const [matches, setMatches] = useState<Match[]>([]);
    const [selectedMaps, setSelectedMaps] = useState<string[]>([]);
    const [selectedSides, setSelectedSides] = useState<string[]>([]); // For side selection
    const [isBo3, setIsBo3] = useState<boolean>(false);

    useEffect(() => {
        const fetchMatches = async () => {
            if (!matchId) return;
            try {
                const matchData = await getMatchById(matchId);
                if (matchData && matchData.length > 0) {
                    setMatches(matchData);
                    setIsBo3(matchData[0].bo3);
                } else {
                    console.error("No match data found.");
                    navigate("/not-found");
                }
            } catch (error) {
                console.error("Error fetching match data:", error);
                navigate("/not-found");
            }
        };

        fetchMatches();
    }, [matchId, navigate]);

    const vetoSteps = isBo3
        ? [
              `${matches[0]?.team1.teamName} removes a map`,
              `${matches[0]?.team2.teamName} removes a map`,
              `${matches[0]?.team1.teamName} picks a map`,
              `${matches[0]?.team2.teamName} chooses side`,
              `${matches[0]?.team2.teamName} picks a map`,
              `${matches[0]?.team1.teamName} chooses side`,
              `${matches[0]?.team2.teamName} removes a map`,
              `${matches[0]?.team1.teamName} removes a map`,
              "A map was left over",
              `${matches[0]?.team2.teamName} chooses side`,
          ]
        : [
              `${matches[0]?.team1.teamName} removes a map`,
              `${matches[0]?.team1.teamName} removes a map`,
              `${matches[0]?.team2.teamName} removes a map`,
              `${matches[0]?.team2.teamName} removes a map`,
              `${matches[0]?.team2.teamName} removes a map`,
              `${matches[0]?.team1.teamName} removes a map`,
              "A map was left over",
              `${matches[0]?.team2.teamName} chooses side`,
          ];

    const handleMapSelection = (stepIndex: number, map: string) => {
        const newSelectedMaps = [...selectedMaps];
        newSelectedMaps[stepIndex] = map;
        setSelectedMaps(newSelectedMaps);
    };

    const handleSideSelection = (stepIndex: number, side: string) => {
        const newSelectedSides = [...selectedSides];
        newSelectedSides[stepIndex] = side;
        setSelectedSides(newSelectedSides);
    };

    const generateFakeTimestamps = (count: number): string[] => {
        const baseTime = new Date("2024-11-20T19:46:00"); // Start time
        const timestamps = [];
        for (let i = 0; i < count; i++) {
            baseTime.setSeconds(baseTime.getSeconds() + 10); // Increment by 10 seconds
            timestamps.push(baseTime.toISOString().substring(11, 19)); // Format HH:mm:ss
        }
        return timestamps;
    };

    const handleSubmit = async () => {
        const timestamps = generateFakeTimestamps(vetoSteps.length);
    
        const vetoLog = vetoSteps
            .map((step, index) => {
                if (step.includes("removes")) {
                    return `${timestamps[index]} - ${step.replace(
                        "removes a map",
                        `removed ${selectedMaps[index]}`
                    )}`;
                } else if (step.includes("picks")) {
                    return `${timestamps[index]} - ${step.replace(
                        "picks a map",
                        `picked ${selectedMaps[index]}`
                    )}`;
                } else if (step.includes("chooses side")) {
                    return `${timestamps[index]} - ${step.replace(
                        "chooses side",
                        `choose to start ${selectedSides[index]}`
                    )}`;
                } else if (step === "A map was left over") {
                    return `${timestamps[index]} - ${selectedMaps[index]} was left over`;
                }
                return "";
            })
            .filter((log) => log !== "") // Remove empty logs
            .join("\n");
    
        console.log("Generated Veto Log:", vetoLog);
    
        // Call the backend endpoint to save the veto log
        try {
            const match = matches[0]; // Assuming all matches share the same veto log
            await axios.post(`${baseURL}/Match/updateVetoLog`, null, {
                params: {
                    matchId: match.matchid,
                    season: match.season,
                    vetoString: vetoLog,
                },
            });
            alert("Veto log saved successfully!");
            navigate(`/match/${matchId}`);
        } catch (error) {
            console.error("Error saving veto log:", error);
            alert("Failed to save the veto log.");
        }
    };
    

    
    const isSaveEnabled = vetoSteps.every((step, index) => {
        if (step.includes("chooses side")) {
            return selectedSides[index] && selectedSides[index].trim() !== "";
        }
        return selectedMaps[index] && selectedMaps[index].trim() !== "";
    });

    if (matches.length === 0) {
        return <div>Loading match details...</div>;
    }

    return (
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
            <h1 style={{ textAlign: "center", margin: "20px 0" }}>
                Add Veto - {isBo3 ? "BO3" : "BO1"}
            </h1>
            <p style={{ textAlign: "center" }}>
                Follow the steps to complete the veto process:
            </p>
            <div>
                {vetoSteps.map((step, index) => (
                    <div key={index} style={{ marginBottom: "20px" }}>
                        <h5>{step}</h5>
                        {step.includes("chooses side") ? (
                            <select
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    fontSize: "16px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                }}
                                value={selectedSides[index] || ""}
                                onChange={(e) =>
                                    handleSideSelection(index, e.target.value)
                                }
                                disabled={
                                    step.includes("chooses side") &&
                                    !selectedMaps[index - 1]
                                }
                            >
                                <option value="">Select a side</option>
                                <option value="CT">CT</option>
                                <option value="T">T</option>
                            </select>
                        ) : (
                            <select
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    fontSize: "16px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                }}
                                value={selectedMaps[index] || ""}
                                onChange={(e) =>
                                    handleMapSelection(index, e.target.value)
                                }
                                disabled={
                                    index > 0 &&
                                    (vetoSteps[index - 1].includes("chooses side")
                                        ? !selectedSides[index - 1]
                                        : !selectedMaps[index - 1])
                                }
                            >
                                <option value="">Select a map</option>
                                {maps
                                    .filter(
                                        (map) =>
                                            !selectedMaps.includes(map) ||
                                            selectedMaps[index] === map
                                    )
                                    .map((map) => (
                                        <option key={map} value={map}>
                                            {map}
                                        </option>
                                    ))}
                            </select>
                        )}
                    </div>
                ))}
            </div>
            <button
                style={{
                    display: "block",
                    width: "100%",
                    padding: "10px",
                    fontSize: "18px",
                    color: "white",
                    backgroundColor: isSaveEnabled ? "green" : "#ccc",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isSaveEnabled ? "pointer" : "not-allowed",
                }}
                onClick={handleSubmit}
                disabled={!isSaveEnabled}
            >
                Save Veto
            </button>
        </div>
    );
};

export default AddVetoPage;
